import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Description,
  EditNote,
  ExpandLess,
  ExpandMore,
  FileDownload,
  InfoOutlined,
  OpenInNew,
  Print,
  UnfoldLess,
  UnfoldMore,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import MonthSelect from "../components/Inputs/MonthSelect";
import EvolutionModal from "../components/modals/EvolutionModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import { patientRegisterUrl } from "../utils/patientRegisterUrl";
import {
  professionalStatementService,
  ProfessionalStatementItem,
  ProfessionalStatementMonth,
  ProfessionalStatementResponse,
  StatementSituation,
} from "../services/professionalStatementService";

/**
 * Produtividade do profissional (RN-226): os atendimentos de UM profissional no período, agrupados
 * por paciente, com presença, evolução e repasse — e o evolutivo dos últimos 6 meses.
 *
 * Um relatório para dois públicos. O Profissional (alcance próprio) vê só a si mesmo: o servidor
 * recorta pelas UEPRs da sessão e a tela nem mostra o filtro. A gestão escolhe o profissional.
 * Mesma régua da Produtividade e repasse (RN-215): o total daqui é a linha dele lá.
 *
 * Ordem de leitura: números do período → tendência → pacientes (onde se age). Cada paciente é
 * uma seção própria, com o nome como título, os atalhos "Evoluir" e "Imprimir" (RN-226c) e as
 * sessões em tabela; entre seções o espaço é generoso e dentro delas, apertado.
 */

const moeda = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const DIAS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const mesLabel = (ym: string) => {
  const [y, m] = ym.split("-");
  return `${MESES[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

/** "seg 01/09" — o dia da semana ajuda o profissional a reconhecer a sessão sem abrir a agenda. */
const dataCurta = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DIAS[dt.getDay()]} ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
};

const competenciaAtual = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const limitesDaCompetencia = (competencia: string) => {
  const [y, m] = competencia.split("-").map((v) => parseInt(v, 10));
  const ultimo = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, "0");
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(ultimo).padStart(2, "0")}` };
};

const SITUACAO: Record<StatementSituation, { label: string; bg: string; fg: string }> = {
  attended: { label: "Realizado", bg: "#d4edda", fg: "#155724" },
  cancelled_patient: { label: "Canc. paciente", bg: "#fff3cd", fg: "#7a5a00" },
  cancelled_professional: { label: "Canc. profissional", bg: "#e9d8fd", fg: "#5a2d91" },
  cancelled_clinic: { label: "Canc. clínica", bg: "#e2e3e5", fg: "#383d41" },
  no_show: { label: "Falta", bg: "#f8d7da", fg: "#721c24" },
  scheduled: { label: "Agendado", bg: "#d1ecf1", fg: "#0c5460" },
  unreported: { label: "Sem presença", bg: "#fdebd0", fg: "#7d4a00" },
};

const COR_REALIZADO = colors.primary;
const COR_CANC_PACIENTE = "#fd7e14";
const COR_CANC_PROFISSIONAL = "#6f42c1";

const painelSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid #e9ecef",
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
};

const chipSx = (bg: string, fg: string) => ({
  height: 22,
  fontSize: "0.72rem",
  fontWeight: 600,
  bgcolor: bg,
  color: fg,
  "& .MuiChip-label": { px: 1 },
});

/** Título de seção da página: mais espaço acima do que abaixo, para o bloco colar no seu título. */
const tituloDeSecaoSx = { fontSize: 13, fontWeight: 700, color: "#495057", textTransform: "uppercase" as const, letterSpacing: "0.04em", mt: 4, mb: 1.25 };

interface GrupoPaciente {
  patientId: string;
  patientName: string;
  itens: ProfessionalStatementItem[];
  realizadas: number;
  canceladas: number;
  pendentes: number;
  evoluidas: number;
  repasse: number;
}

/** Fila de sessões a evoluir de um paciente, percorrida uma a uma pelo formulário. */
interface FilaDeEvolucao {
  patientId: string;
  patientName: string;
  scheduleIds: string[];
  posicao: number;
  gravadas: number;
}

/**
 * Evolutivo em linhas: três séries (realizados, cancelados pelo paciente, pelo profissional) nos
 * últimos meses, com o valor em cada ponto — a tendência se lê pela inclinação, o número pelo rótulo.
 * Desenhado em pixels medidos do contêiner (não em viewBox escalado) para o texto manter o tamanho
 * em qualquer largura; no celular a área rola na horizontal a partir de 420px.
 */
const GraficoDeLinhas: React.FC<{ meses: ProfessionalStatementMonth[] }> = ({ meses }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setLargura(el.clientWidth);
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const series = [
    { chave: "attended" as const, label: "Realizados", cor: COR_REALIZADO },
    { chave: "cancelledByPatient" as const, label: "Canc. paciente", cor: COR_CANC_PACIENTE },
    { chave: "cancelledByProfessional" as const, label: "Canc. profissional", cor: COR_CANC_PROFISSIONAL },
  ];

  const w = Math.max(largura, 420);
  const h = 230;
  const pad = { top: 22, right: 24, bottom: 48, left: 36 };
  const maximo = Math.max(1, ...meses.flatMap((m) => series.map((s) => m[s.chave])));
  // Teto "redondo" acima do maior valor, para o ponto mais alto não encostar na borda
  const passo = maximo <= 5 ? 1 : maximo <= 20 ? 5 : maximo <= 50 ? 10 : 20;
  const teto = Math.ceil((maximo + passo * 0.25) / passo) * passo;
  const n = meses.length;
  const x = (i: number) => (n === 1 ? w / 2 : pad.left + (i * (w - pad.left - pad.right)) / (n - 1));
  const y = (v: number) => pad.top + (h - pad.top - pad.bottom) * (1 - v / teto);
  const linhasGuia = Array.from({ length: Math.floor(teto / passo) + 1 }, (_, i) => i * passo);

  return (
    <Box ref={ref} sx={{ width: "100%", overflowX: "auto" }}>
      {largura > 0 && (
        <svg width={w} height={h} role="img" aria-label="Evolutivo mensal de realizados e cancelamentos" style={{ display: "block", fontFamily: "inherit" }}>
          {linhasGuia.map((v) => (
            <g key={v}>
              <line x1={pad.left} x2={w - pad.right} y1={y(v)} y2={y(v)} stroke="#edf0f2" strokeWidth={1} />
              <text x={pad.left - 8} y={y(v) + 3.5} textAnchor="end" fontSize={10} fill="#adb5bd" style={{ fontVariantNumeric: "tabular-nums" }}>{v}</text>
            </g>
          ))}
          {series.map((s) => {
            const pontos = meses.map((m, i) => `${x(i)},${y(m[s.chave])}`).join(" ");
            return (
              <g key={s.chave}>
                <polyline points={pontos} fill="none" stroke={s.cor} strokeWidth={s.chave === "attended" ? 2.5 : 1.75} strokeLinejoin="round" strokeLinecap="round" />
                {meses.map((m, i) => (
                  <g key={m.month}>
                    <circle cx={x(i)} cy={y(m[s.chave])} r={s.chave === "attended" ? 4 : 3} fill="#fff" stroke={s.cor} strokeWidth={2} />
                    {/* Rótulo acima do ponto; zero fica mudo para não poluir a base */}
                    {m[s.chave] > 0 && (
                      <text x={x(i)} y={y(m[s.chave]) - 8} textAnchor="middle" fontSize={11} fontWeight={700} fill={s.cor} style={{ fontVariantNumeric: "tabular-nums" }}>
                        {m[s.chave]}
                      </text>
                    )}
                  </g>
                ))}
              </g>
            );
          })}
          {meses.map((m, i) => (
            <g key={m.month}>
              <text x={x(i)} y={h - pad.bottom + 18} textAnchor="middle" fontSize={12} fontWeight={600} fill="#212529">{mesLabel(m.month)}</text>
              <text x={x(i)} y={h - pad.bottom + 34} textAnchor="middle" fontSize={10.5} fill="#6c757d" style={{ fontVariantNumeric: "tabular-nums" }}>{moeda(m.payout)}</text>
            </g>
          ))}
        </svg>
      )}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1, px: 0.5 }}>
        {series.map((s) => (
          <Box key={s.chave} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, color: "#495057" }}>
            <Box sx={{ width: 18, height: 0, borderTop: `2.5px solid ${s.cor}`, borderRadius: 2 }} />
            {s.label}
          </Box>
        ))}
        <Typography sx={{ fontSize: 11.5, color: "#adb5bd", ml: "auto" }}>Abaixo de cada mês, o repasse das sessões realizadas.</Typography>
      </Box>
    </Box>
  );
};

const ReportProfessionalStatement: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const padrao = limitesDaCompetencia(competenciaAtual());
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [de, setDe] = useState(padrao.from);
  const [ate, setAte] = useState(padrao.to);
  const [profissionalId, setProfissionalId] = useState("");

  const [data, setData] = useState<ProfessionalStatementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [showNames, setShowNames] = useState<boolean | null>(null);
  const [fechados, setFechados] = useState<string[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [fila, setFila] = useState<FilaDeEvolucao | null>(null);
  // Paciente em impressão: enquanto preenchido, só a seção dele vai para o papel
  const [imprimindo, setImprimindo] = useState<string | null>(null);

  const escopoProprio = data?.scope === "own";
  // Quem vê só a própria agenda já conhece os pacientes pelo nome; para a gestão, mascarado por padrão
  const nomesVisiveis = showNames ?? escopoProprio;
  const nome = (n: string) => (nomesVisiveis ? n : maskName(n));

  const carregar = useCallback(
    async (p?: { from: string; to: string; professionalId: string }) => {
      const params = p ?? { from: de, to: ate, professionalId: profissionalId };
      if (!params.from || !params.to) return;
      setLoading(true);
      setErro("");
      try {
        setData(await professionalStatementService.get(params));
        setSelecionadas(new Set());
      } catch {
        setErro("Não foi possível carregar o relatório. Tente novamente.");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [de, ate, profissionalId],
  );

  useEffect(() => {
    carregar();
    // carga inicial; as demais saem dos filtros
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mudarCompetencia = (v: string) => {
    setCompetencia(v);
    if (!v) return;
    const b = limitesDaCompetencia(v);
    setDe(b.from);
    setAte(b.to);
    carregar({ from: b.from, to: b.to, professionalId: profissionalId });
  };
  const mudarDe = (v: string) => {
    setDe(v);
    setCompetencia("");
    if (v && ate) carregar({ from: v, to: ate, professionalId: profissionalId });
  };
  const mudarAte = (v: string) => {
    setAte(v);
    setCompetencia("");
    if (de && v) carregar({ from: de, to: v, professionalId: profissionalId });
  };
  const mudarProfissional = (v: string) => {
    setProfissionalId(v);
    carregar({ from: de, to: ate, professionalId: v });
  };
  const limpar = () => {
    setCompetencia(competenciaAtual());
    setDe(padrao.from);
    setAte(padrao.to);
    setProfissionalId("");
    carregar({ from: padrao.from, to: padrao.to, professionalId: "" });
  };
  const temFiltro = competencia !== competenciaAtual() || de !== padrao.from || ate !== padrao.to || profissionalId !== "";

  const exportar = async () => {
    try {
      const blob = await professionalStatementService.exportXlsx({ from: de, to: ate, professionalId: profissionalId });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `produtividade_${de}_${ate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Não foi possível gerar a planilha.", "error");
    }
  };

  const grupos = useMemo<GrupoPaciente[]>(() => {
    const porPaciente = new Map<string, GrupoPaciente>();
    for (const i of data?.items ?? []) {
      const chave = i.patientId || i.patientName;
      let g = porPaciente.get(chave);
      if (!g) {
        g = { patientId: i.patientId, patientName: i.patientName, itens: [], realizadas: 0, canceladas: 0, pendentes: 0, evoluidas: 0, repasse: 0 };
        porPaciente.set(chave, g);
      }
      g.itens.push(i);
      if (i.situation === "attended") g.realizadas++;
      if (i.situation.startsWith("cancelled")) g.canceladas++;
      if (i.evolutionPending) g.pendentes++;
      if (i.evolved) g.evoluidas++;
      g.repasse += i.payout;
    }
    return Array.from(porPaciente.values())
      .map((g) => ({ ...g, itens: [...g.itens].sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)) }))
      .sort((a, b) => a.patientName.localeCompare(b.patientName, "pt-BR", { sensitivity: "base" }));
  }, [data]);

  const chaveDoGrupo = (g: GrupoPaciente) => g.patientId || g.patientName;
  const todosFechados = grupos.length > 0 && fechados.length === grupos.length;
  const alternarGrupo = (chave: string) =>
    setFechados((atual) => (atual.includes(chave) ? atual.filter((c) => c !== chave) : [...atual, chave]));

  const alternarSessao = (id: string) =>
    setSelecionadas((atual) => {
      const prox = new Set(atual);
      if (prox.has(id)) prox.delete(id);
      else prox.add(id);
      return prox;
    });
  const alternarGrupoInteiro = (g: GrupoPaciente) =>
    setSelecionadas((atual) => {
      const prox = new Set(atual);
      const todas = g.itens.every((i) => prox.has(i.scheduleId));
      g.itens.forEach((i) => (todas ? prox.delete(i.scheduleId) : prox.add(i.scheduleId)));
      return prox;
    });

  const marcadasDoGrupo = (g: GrupoPaciente) => g.itens.filter((i) => selecionadas.has(i.scheduleId));

  /** Evoluções das sessões MARCADAS: a impressão só considera o que foi flegado (pedido do Hilton, 03/10). */
  const evolucoesParaImprimir = (g: GrupoPaciente): string[] =>
    marcadasDoGrupo(g).map((i) => i.evolutionId).filter((id): id is string => !!id);

  /** Evoluir uma sessão: abre o formulário já nela (botão da linha, só quando o status permite). */
  const evoluirSessao = (g: GrupoPaciente, i: ProfessionalStatementItem) => {
    if (!g.patientId || !i.canEvolve) return;
    setFila({ patientId: g.patientId, patientName: g.patientName, scheduleIds: [i.scheduleId], posicao: 0, gravadas: 0 });
  };

  const evolucaoGravada = (mensagem: string) => {
    if (!fila) return;
    const proxima = fila.posicao + 1;
    if (proxima < fila.scheduleIds.length) {
      showToast(`${mensagem} Próxima sessão: ${proxima + 1} de ${fila.scheduleIds.length}.`);
      setFila({ ...fila, posicao: proxima, gravadas: fila.gravadas + 1 });
      return;
    }
    showToast(fila.scheduleIds.length > 1 ? `${fila.scheduleIds.length} evoluções registradas.` : mensagem);
    setFila(null);
    carregar();
  };

  const fecharEvolucao = () => {
    const gravouAlguma = (fila?.gravadas ?? 0) > 0;
    setFila(null);
    if (gravouAlguma) carregar();
  };

  /**
   * Impressão POR PACIENTE (pedido do Hilton, 03/10): só a seção dele e só as sessões MARCADAS, com
   * profissional e período no topo. Sem marcação não imprime; não depende de haver evolução registrada.
   */
  const imprimirPaciente = (g: GrupoPaciente) => {
    if (marcadasDoGrupo(g).length === 0) return;
    setImprimindo(chaveDoGrupo(g));
    // um ciclo de render para as classes de impressão entrarem antes do diálogo
    setTimeout(() => {
      window.print();
      setImprimindo(null);
    }, 200);
  };

  const imprimirEvolucoes = (g: GrupoPaciente) => {
    const ids = evolucoesParaImprimir(g);
    if (ids.length === 0 || !g.patientId) return;
    const clinic = new URLSearchParams(window.location.search).get("clinic") || "ninho";
    window.open(`?page=evolutions-print&patientId=${g.patientId}&ids=${ids.join(",")}&clinic=${clinic}`, "_blank");
  };

  const t = data?.totals;
  /** Fora do papel quando a impressão é de um paciente só */
  const soNoGeral = imprimindo ? "no-print" : "c4u-print-block";
  const dataBr = (ymd: string) => ymd.split("-").reverse().join("/");
  const precisaEscolher = data?.scope === "entity" && !data.professionalId;

  return (
    <Box className="c4u-print-page" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Produtividade do profissional
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                {escopoProprio
                  ? "Seus atendimentos no período, por paciente: presença, evolução e repasse."
                  : "Os atendimentos de um profissional no período, por paciente: presença, evolução e repasse."}
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect label="Competência" value={competencia} onChange={mudarCompetencia} sx={filterFieldSx} />
                <TextField size="small" type="date" label="De" value={de} onChange={(e) => mudarDe(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="Até" value={ate} onChange={(e) => mudarAte(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                {data?.scope === "entity" && (
                  <TextField select size="small" label="Profissional" value={profissionalId} onChange={(e) => mudarProfissional(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                    <MenuItem value="">Selecione…</MenuItem>
                    {/* So vinculo ativo: o relatorio e de quem esta atendendo (pedido do Hilton, 02/10) */}
                    {data.professionals.filter((p) => p.active !== false).map((p) => (
                      <MenuItem key={p.externalId} value={p.externalId}>
                        {nome(p.name)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={limpar} disabled={!temFiltro || loading} />
                {!escopoProprio && (
                  <Button
                    variant="contained" size="small" onClick={() => setShowNames(!nomesVisiveis)}
                    startIcon={nomesVisiveis ? <VisibilityOff /> : <Visibility />}
                    sx={{ textTransform: "none", backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5a6268" }, height: 40 }}
                  >
                    {nomesVisiveis ? "Ocultar nomes" : "Exibir nomes"}
                  </Button>
                )}
                <Tooltip title="Imprimir" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || grupos.length === 0} aria-label="Imprimir">
                      <Print />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar para Excel" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={exportar} disabled={loading || grupos.length === 0} aria-label="Exportar para Excel">
                      <FileDownload />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box className="c4u-info-box no-print">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Repasse é o valor gravado em cada sessão em que o paciente compareceu — a mesma conta da Produtividade e
                repasse. "Evoluir" aparece na sessão cujo status permite evolução (Configurações da agenda) e que ainda não
                tem uma. "Imprimir" é por paciente e considera só as sessões marcadas: marque as linhas (ou a caixa ao lado do
                nome, para todas) e imprima; "Evoluções" abre o texto das evoluções já registradas nas sessões marcadas. O nome do paciente abre o cadastro em nova janela.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : erro ? (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{erro}</Typography>
            ) : precisaEscolher ? (
              <Typography sx={{ textAlign: "center", color: "#6c757d", py: 6, fontSize: 14 }}>
                Escolha um profissional para ver os atendimentos do período.
              </Typography>
            ) : !data || !t ? null : (
              <>
                {/* ================= NÚMEROS DO PERÍODO ================= */}
                <Typography sx={{ ...tituloDeSecaoSx, mt: 2.5 }} className={imprimindo ? "no-print" : undefined}>
                  {escopoProprio ? "Seu período" : nome(data.professionalName) || "Período"}
                </Typography>
                <Box sx={{ ...summaryCardsRowSx, mb: 0 }} className={soNoGeral}>
                  <ReportSummaryCard label="Realizados" value={String(t.attended)} color="#28a745" sub={`${t.attendanceRate}% de ${t.totalSchedules} agendados`} />
                  <ReportSummaryCard label="Cancel. paciente" value={String(t.cancelledByPatient)} color={COR_CANC_PACIENTE} />
                  <ReportSummaryCard label="Cancel. profissional" value={String(t.cancelledByProfessional)} color={COR_CANC_PROFISSIONAL} />
                  <ReportSummaryCard label="Faltas" value={String(t.noShow)} color="#dc3545" />
                  <ReportSummaryCard
                    label="Evoluções pendentes"
                    value={String(t.evolutionPending)}
                    color={t.evolutionPending > 0 ? "#dc3545" : "#28a745"}
                    sub={`${t.evolvedRate}% dos realizados evoluídos`}
                  />
                  <ReportSummaryCard label="Repasse" value={moeda(t.payout)} color={colors.primary} sub="sessões realizadas" />
                </Box>

                {/* ================= EVOLUTIVO ================= */}
                <Typography sx={tituloDeSecaoSx} className={imprimindo ? "no-print" : undefined}>Últimos 6 meses</Typography>
                <Paper elevation={0} sx={{ ...painelSx, p: { xs: 1.5, sm: 2.5 } }} className={soNoGeral}>
                  <GraficoDeLinhas meses={data.monthly} />
                </Paper>

                {/* ================= POR PACIENTE ================= */}
                {grupos.length === 0 ? (
                  <Typography sx={{ textAlign: "center", color: "#6c757d", py: 6, fontSize: 14 }}>
                    Nenhum atendimento no período.
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 1 }} className={imprimindo ? "no-print" : undefined}>
                      <Typography sx={tituloDeSecaoSx}>
                        Por paciente
                        <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#6c757d", ml: 1 }}>
                          {grupos.length} paciente{grupos.length === 1 ? "" : "s"} · {data.items.length} sessão{data.items.length === 1 ? "" : "ões"}
                          {selecionadas.size > 0 && ` · ${selecionadas.size} marcada${selecionadas.size === 1 ? "" : "s"}`}
                        </Box>
                      </Typography>
                      <Button size="small" className="no-print" startIcon={todosFechados ? <UnfoldMore /> : <UnfoldLess />} onClick={() => setFechados(todosFechados ? [] : grupos.map(chaveDoGrupo))} sx={{ textTransform: "none" }}>
                        {todosFechados ? "Abrir todos" : "Recolher todos"}
                      </Button>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {grupos.map((g) => {
                        const chave = chaveDoGrupo(g);
                        const emImpressao = imprimindo === chave;
                        // Recolhido na tela, aberto no papel: quem imprime o paciente quer as sessões
                        const aberto = !fechados.includes(chave) || emImpressao;
                        const marcadas = marcadasDoGrupo(g).length;
                        const paraImprimir = evolucoesParaImprimir(g).length;
                        return (
                          <Paper key={chave} component="section" elevation={0} sx={{ ...painelSx, overflow: "hidden" }} className={imprimindo && !emImpressao ? "no-print" : "c4u-print-block"} aria-label={g.patientName}>
                            {emImpressao && (
                              <Box sx={{ display: "none", "@media print": { display: "block" }, px: 2.5, pt: 2, fontSize: 12, color: "#495057" }}>
                                <strong>Profissional:</strong> {data.professionalName || "—"} · <strong>Período:</strong> {dataBr(de)} a {dataBr(ate)}
                                {` · ${marcadas} de ${g.itens.length} sessões`}
                              </Box>
                            )}
                            {/* Cabeçalho do paciente: nome como título, resumo logo abaixo; ações à direita em linha própria no celular */}
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: { xs: 1.5, sm: 2.5 }, pt: 2, pb: 1.5, flexWrap: "wrap" }}>
                              <Checkbox
                                size="small"
                                className="no-print"
                                checked={marcadas > 0 && marcadas === g.itens.length}
                                indeterminate={marcadas > 0 && marcadas < g.itens.length}
                                onChange={() => alternarGrupoInteiro(g)}
                                inputProps={{ "aria-label": `Marcar todas as sessões de ${g.patientName}` }}
                                sx={{ p: 0.5, mt: 0.25, ml: -0.5 }}
                              />
                              <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                  {/* O nome é o link: abre o cadastro em nova janela sem perder o relatório (pedido do Hilton, 02/10) */}
                                  {g.patientId ? (
                                    <Tooltip title="Abrir cadastro do paciente em nova janela" arrow>
                                      <Typography
                                        component="a"
                                        href={patientRegisterUrl(g.patientId)}
                                        target="_blank"
                                        rel="noopener"
                                        sx={{ fontSize: 17, fontWeight: 700, color: "#212529", lineHeight: 1.3, overflowWrap: "anywhere", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.5, "&:hover": { color: colors.primary, textDecoration: "underline", textUnderlineOffset: "3px" } }}
                                      >
                                        {nome(g.patientName)}
                                        <OpenInNew className="no-print" sx={{ fontSize: 15, color: colors.primary }} />
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, color: "#212529", lineHeight: 1.3, overflowWrap: "anywhere" }}>
                                      {nome(g.patientName)}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.25rem 0.75rem", mt: 0.5, fontSize: 12.5, color: "#6c757d" }}>
                                  <span>{g.itens.length} sess{g.itens.length === 1 ? "ão" : "ões"}</span>
                                  <span>·</span>
                                  <span>{g.realizadas} realizada{g.realizadas === 1 ? "" : "s"}</span>
                                  {g.canceladas > 0 && <><span>·</span><span>{g.canceladas} cancelada{g.canceladas === 1 ? "" : "s"}</span></>}
                                  {g.pendentes > 0 && (
                                    <Chip size="small" label={`${g.pendentes} sem evolução`} sx={{ ...chipSx("#f8d7da", "#721c24"), height: 20, fontSize: "0.7rem" }} />
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: "right", flex: "0 0 auto" }}>
                                <Typography sx={{ fontSize: 11, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.04em" }}>Repasse</Typography>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.primary, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
                                  {moeda(g.repasse)}
                                </Typography>
                              </Box>
                              <Box className="no-print" sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "0 0 auto", ml: { xs: "auto", sm: 1 } }}>
                                {paraImprimir > 0 && (
                                  <Tooltip title={`Imprimir o texto de ${paraImprimir} evolução${paraImprimir === 1 ? "" : "ões"} das sessões marcadas`} arrow>
                                    <Button size="small" variant="text" startIcon={<Description />} onClick={() => imprimirEvolucoes(g)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                                      Evoluções ({paraImprimir})
                                    </Button>
                                  </Tooltip>
                                )}
                                <Tooltip title={marcadas > 0 ? `Imprimir ${marcadas === 1 ? "a sessão marcada" : `as ${marcadas} sessões marcadas`} deste paciente` : "Marque as sessões que deseja imprimir"} arrow>
                                  <span>
                                    <Button size="small" variant="outlined" startIcon={<Print />} disabled={marcadas === 0} onClick={() => imprimirPaciente(g)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                                      Imprimir{marcadas > 0 ? ` (${marcadas})` : ""}
                                    </Button>
                                  </span>
                                </Tooltip>
                                <Tooltip title={aberto ? "Recolher sessões" : `Ver ${g.itens.length} sess${g.itens.length === 1 ? "ão" : "ões"}`} arrow>
                                  <IconButton size="small" onClick={() => alternarGrupo(chave)} aria-expanded={aberto} aria-label={aberto ? "Recolher sessões" : "Mostrar sessões"} sx={{ color: "#495057" }}>
                                    {aberto ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>

                            {aberto && (
                              <Box sx={{ borderTop: "1px solid #e9ecef", px: { xs: 0.5, sm: 1.5 }, py: 1 }}>
                                <Box className="c4u-table">
                                  <Box className="c4u-table__header">
                                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 200px" }}>Data</Box>
                                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 200px" }}>Serviço</Box>
                                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 240px" }}>Situação</Box>
                                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 120px" }}>Evolução</Box>
                                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px", justifyContent: "flex-end" }}>Repasse</Box>
                                  </Box>
                                  {g.itens.map((i) => {
                                    const s = SITUACAO[i.situation];
                                    const marcada = selecionadas.has(i.scheduleId);
                                    const detalhe = [i.confirmation, i.presence].filter(Boolean).join(" · ");
                                    return (
                                      <Box key={i.scheduleId} className={`c4u-table__row${emImpressao && marcadas > 0 && !marcada ? " no-print" : ""}`} sx={{ minHeight: 48, bgcolor: marcada ? "#eef8fa !important" : undefined, "@media print": { bgcolor: "transparent !important" } }}>
                                        {/* Checkbox dentro da célula da data: no celular a primeira célula vira o título do cartão */}
                                        <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 200px", display: "flex", alignItems: "center", gap: 0.75 }}>
                                          <Checkbox size="small" className="no-print" checked={marcada} onChange={() => alternarSessao(i.scheduleId)} inputProps={{ "aria-label": `Marcar sessão de ${dataCurta(i.date)} às ${i.startTime}` }} sx={{ p: 0.5, ml: -0.5 }} />
                                          <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                            {dataCurta(i.date)}
                                            <Box component="span" sx={{ fontWeight: 400, color: "#6c757d", ml: 1 }}>{i.startTime}–{i.endTime}</Box>
                                          </Typography>
                                        </Box>
                                        <Box className="admin-plans-cell" data-label="Serviço" sx={{ flex: "1 1 200px", minWidth: 0 }}>
                                          <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{i.serviceName}</Typography>
                                        </Box>
                                        <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 240px", minWidth: 0 }}>
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25, minWidth: 0 }}>
                                            <Chip size="small" label={s.label} sx={chipSx(s.bg, s.fg)} />
                                            {detalhe && <Typography sx={{ fontSize: 11.5, color: "#6c757d", overflowWrap: "anywhere" }}>{detalhe}</Typography>}
                                          </Box>
                                        </Box>
                                        <Box className="admin-plans-cell" data-label="Evolução" sx={{ flex: "0 0 120px" }}>
                                          {i.evolved ? (
                                            <Chip size="small" label="Evoluída" sx={chipSx("#d4edda", "#155724")} />
                                          ) : i.canEvolve ? (
                                            <>
                                              {/* Realizada sem evolução é a pendência de verdade: botão cheio; nos demais status permitidos, contorno */}
                                              <Button size="small" variant={i.evolutionPending ? "contained" : "outlined"} className="no-print" startIcon={<EditNote />} onClick={() => evoluirSessao(g, i)} sx={{ textTransform: "none", minHeight: 28, py: 0.25, px: 1, fontSize: 12.5, whiteSpace: "nowrap", ...(i.evolutionPending ? { backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } } : {}) }}>
                                                Evoluir
                                              </Button>
                                              {i.evolutionPending && <Chip size="small" label="Pendente" sx={{ ...chipSx("#f8d7da", "#721c24"), display: "none", "@media print": { display: "inline-flex" } }} />}
                                            </>
                                          ) : (
                                            <Tooltip title="O status desta sessão não permite evolução (Configurações da agenda)" arrow>
                                              <Typography sx={{ fontSize: 12, color: "#adb5bd", cursor: "help" }}>—</Typography>
                                            </Tooltip>
                                          )}
                                        </Box>
                                        <Box className="admin-plans-cell" data-label="Repasse" sx={{ flex: "0 0 110px", justifyContent: "flex-end", fontVariantNumeric: "tabular-nums" }}>
                                          <Typography sx={{ fontSize: 13, color: i.payout > 0 ? "#212529" : "#adb5bd" }}>
                                            {i.payout > 0 ? moeda(i.payout) : "—"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  </>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      {fila && (
        <EvolutionModal
          key={`${fila.patientId}-${fila.scheduleIds[fila.posicao]}`}
          isOpen
          patientId={fila.patientId}
          evolution={null}
          initialScheduleId={fila.scheduleIds[fila.posicao]}
          onClose={fecharEvolucao}
          onSaved={evolucaoGravada}
        />
      )}

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />

      <FooterInternal />
    </Box>
  );
};

export default ReportProfessionalStatement;
